import { useEffect, useState } from "react";
import { FileSpreadsheet, History } from "lucide-react";
import { toast } from "sonner";
import ImportUploadCard from "./components/ImportUploadCard";
import ImportPreviewDialog from "./components/ImportPreviewDialog";
import BudgetCodeMatrix from "./components/BudgetCodeMatrix";
import { estApi } from "./lib/api";

export default function CostCodesPage() {
  const [preview, setPreview] = useState(null);   // { kind, data }
  const [logs, setLogs] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    estApi.getImportLogs().then(setLogs).catch(() => {});
  }, [refreshKey]);

  const commit = async () => {
    const { kind, data } = preview;
    const res = kind === "cost_codes"
      ? await estApi.commitCostCodes(data.preview_id)
      : await estApi.commitCostTypes(data.preview_id);
    const c = res.counts;
    toast.success(
      `${kind === "cost_codes" ? "Cost codes" : "Cost types"} imported — ` +
      `${c.added} added, ${c.updated} updated, ${c.deactivated} deactivated` +
      (c.budget_codes_created ? `, ${c.budget_codes_created} budget codes created` : "")
    );
    setPreview(null);
    setRefreshKey((k) => k + 1);
  };

  const codeCols = [
    { key: "code", label: "Code" }, { key: "name", label: "Description" },
    { key: "tier1", label: "Tier 1" }, { key: "tier2", label: "Tier 2" },
  ];

  return (
    <div className="page-body" data-testid="cost-codes-page">
      <h2 className="text-lg font-semibold text-slate-900">Cost Codes</h2>
      <p className="text-sm text-slate-500 mt-0.5">
        Cost codes and cost types are the backbone of every estimate line. Imports never delete —
        codes missing from a new file are marked inactive.
      </p>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ImportUploadCard
          testid="import-cost-codes"
          title="Cost Codes"
          description="Standard cost code list export. Codes without a dash are division headers and can't be assigned to a line."
          hint="Columns: Code, Description"
          accept=".csv,text/csv"
          onPreview={async (file) => setPreview({ kind: "cost_codes", data: await estApi.previewCostCodes(file) })}
        />
        <ImportUploadCard
          testid="import-cost-types"
          title="Cost Types"
          description="Cost Types export. M, L, P, U and friends. BUR is stored but never pushed — the labor rate is burdened."
          hint="Columns: Code, Description"
          accept=".csv,text/csv"
          onPreview={async (file) => setPreview({ kind: "cost_types", data: await estApi.previewCostTypes(file) })}
        />
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-slate-800">Budget Code Matrix</h3>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          Every postable cost code × cost type pair. Uncheck the pairs ABS doesn't use (e.g. Labor on a
          material-only code) — unchecked pairs won't be offered on estimate lines.
        </p>
        <BudgetCodeMatrix key={refreshKey} />
      </div>

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <History className="h-4 w-4 text-slate-400" /> Import Log
        </h3>
        <div className="mt-2 rounded-md border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-xs" data-testid="import-log-table">
            <thead className="bg-slate-50">
              <tr>
                {["When", "Kind", "File", "Counts", "User"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-medium text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400">No imports yet</td></tr>
              )}
              {logs.map((l) => (
                <tr key={l.id} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">
                    {new Date(l.created_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1 text-slate-700">
                      <FileSpreadsheet className="h-3.5 w-3.5 text-slate-400" />{l.kind}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-slate-600 truncate max-w-[220px]">{l.file_name}</td>
                  <td className="px-3 py-1.5 text-slate-500 font-mono text-[11px]">
                    {Object.entries(l.counts || {}).map(([k, v]) => `${k}:${v}`).join("  ")}
                  </td>
                  <td className="px-3 py-1.5 text-slate-500">{l.user_email}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {preview && (
        <ImportPreviewDialog
          open
          onOpenChange={(o) => !o && setPreview(null)}
          title={preview.kind === "cost_codes" ? "Cost Code Import — Preview" : "Cost Type Import — Preview"}
          preview={preview.data}
          onCommit={commit}
          extraTables={[
            { testid: "preview-added", title: "Added", rows: preview.data.added || [], columns: codeCols },
            { testid: "preview-updated", title: "Updated", rows: preview.data.updated || [], columns: codeCols },
            { testid: "preview-deactivated", title: "Marked inactive (missing from file)", rows: preview.data.deactivated || [], columns: codeCols.slice(0, 2) },
          ]}
        >
          {preview.data.division_headers?.length > 0 && (
            <p className="mt-3 text-xs text-slate-500" data-testid="preview-headers-note">
              Division headers (non-postable): {" "}
              <span className="font-mono text-slate-700">{[...preview.data.division_headers].sort().join(", ")}</span>
            </p>
          )}
        </ImportPreviewDialog>
      )}
    </div>
  );
}
